// src/pages/ReportIssue/components/IssueDetailsSection.js
import React from "react";
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    Box,
    Grid,
} from "@mui/material";
import { HomeWork as HomeWorkIcon } from "@mui/icons-material";

// Priority levels with descriptions
const priorities = [
    {
        value: 'Low',
        description: 'Issue is not urgent and doesn\'t affect daily activities'
    },
    {
        value: 'Medium',
        description: 'Issue causes inconvenience but doesn\'t prevent essential activities'
    },
    {
        value: 'High',
        description: 'Issue requires urgent attention (safety concerns, major leaks, etc.)'
    }
];

const IssueDetailsSection = ({
                                 problemTypes = [],
                                 issues = [],
                                 selectedProblemType = "",
                                 selectedIssue = "",
                                 selectedPriority = "Medium",
                                 loadingProblemTypes = false,
                                 loadingIssues = false,
                                 setSelectedProblemType = () => {},
                                 setSelectedIssue = () => {},
                                 setSelectedPriority = () => {},
                                 getIssues = () => {}
                             }) => {
    return (
        <Box mb={4}>
            <Box
                display="flex"
                alignItems="center"
                gap={1}
                mb={2}
            >
                <HomeWorkIcon sx={{ color: '#3B82F6' }} />
                <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: '#3A5A78' }}
                >
                    Issue Details
                </Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <FormControl
                        required
                        fullWidth
                        error={selectedProblemType === ""}
                        disabled={loadingProblemTypes}
                    >
                        <InputLabel
                            id="problem-type-label"
                            sx={{ backgroundColor: 'white', px: 1 }}
                        >
                            Type of Problem
                        </InputLabel>
                        <Select
                            labelId="problem-type-label"
                            id="problem-type-select"
                            value={selectedProblemType}
                            label="Type of Problem"
                            onChange={(e) => {
                                setSelectedProblemType(e.target.value);
                                getIssues(e.target.value);
                                setSelectedIssue("");
                            }}
                            sx={{
                                borderRadius: '10px',
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: selectedProblemType === "" ? "#d32f2f" : undefined
                                }
                            }}
                        >
                            {loadingProblemTypes ? (
                                <MenuItem value="">
                                    <em>Loading...</em>
                                </MenuItem>
                            ) : problemTypes && problemTypes.length > 0 ? (
                                problemTypes.map((type) => {
                                    // Handle both Map and plain object formats
                                    let problem;
                                    if (type instanceof Map) {
                                        problem = type;
                                    } else {
                                        problem = new Map(Object.entries(type));
                                    }

                                    const id = problem.get("id") || type.id;
                                    const problemType = problem.get("problem_type") || type.problem_type;

                                    return (
                                        <MenuItem
                                            key={id}
                                            value={id}
                                        >
                                            {problemType}
                                        </MenuItem>
                                    );
                                })
                            ) : (
                                <MenuItem value="">
                                    <em>No problem types found</em>
                                </MenuItem>
                            )}
                        </Select>
                        {selectedProblemType === "" && (
                            <FormHelperText error>
                                Please select a problem type
                            </FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <FormControl
                        required
                        fullWidth
                        error={selectedIssue === "" && selectedProblemType !== ""}
                        disabled={loadingIssues || selectedProblemType === ""}
                    >
                        <InputLabel
                            id="issue-label"
                            sx={{ backgroundColor: 'white', px: 1 }}
                        >
                            Specific Issue
                        </InputLabel>
                        <Select
                            labelId="issue-label"
                            id="issue-select"
                            value={selectedIssue}
                            label="Specific Issue"
                            onChange={(e) => {
                                setSelectedIssue(e.target.value);
                            }}
                            sx={{
                                borderRadius: '10px',
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: (selectedIssue === "" && selectedProblemType !== "")
                                        ? "#d32f2f"
                                        : undefined
                                }
                            }}
                        >
                            {loadingIssues ? (
                                <MenuItem value="">
                                    <em>Loading...</em>
                                </MenuItem>
                            ) : issues && issues.length > 0 ? (
                                issues.map((issue) => {
                                    // Handle both Map and plain object formats
                                    let problem;
                                    if (issue instanceof Map) {
                                        problem = issue;
                                    } else {
                                        problem = new Map(Object.entries(issue));
                                    }

                                    const id = problem.get("id") || issue.id;
                                    const problemText = problem.get("problem") || issue.problem;

                                    return (
                                        <MenuItem
                                            key={id}
                                            value={id}
                                        >
                                            {problemText}
                                        </MenuItem>
                                    );
                                })
                            ) : (
                                <MenuItem value="">
                                    <em>No issues found</em>
                                </MenuItem>
                            )}
                        </Select>
                        {selectedIssue === "" && selectedProblemType !== "" && (
                            <FormHelperText error>
                                Please select a specific issue
                            </FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <FormControl required fullWidth error={!selectedPriority}>
                        <InputLabel id="priority-label" sx={{ backgroundColor: 'white', px: 1 }}>Priority Level</InputLabel>
                        <Select
                            labelId="priority-label"
                            value={selectedPriority}
                            label="Priority Level"
                            onChange={(e) => setSelectedPriority(e.target.value)}
                            sx={{ borderRadius: '10px' }}
                        >
                            {priorities.map((priority) => (
                                <MenuItem key={priority.value} value={priority.value}>
                                    {priority.value}
                                </MenuItem>
                            ))}
                        </Select>
                        {!selectedPriority && (
                            <FormHelperText error>
                                Please select a priority level
                            </FormHelperText>
                        )}
                    </FormControl>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                        {priorities.find(p => p.value === selectedPriority)?.description}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

export default IssueDetailsSection;